import React, { useMemo } from 'react';
import { formatCurrency, getCurrentDate, sumSubsidies, sumAccruedBalance, sumSalaryExpense, calculateSubsidyTotals, calculateActivitySubsidyPercentage, calculateSalaryExpenseBySubsidy } from '../../../../shared/helpers';

function BudgetTable({ pme, yearSelected, selectedSubsidy, establishmentList, pmeList, isAdmin, isHolder, userData, dataSearch }) {
    const tableData = useMemo(() => {
        if (!pme) return [];

        let relevantEstablishments = [];
        if (isAdmin) {
            // Para admin, mostrar solo los establecimientos con datasearch
            const holderEstablishments = establishmentList.filter(est =>
                est.projectId === dataSearch.holder.id
            );
            relevantEstablishments = holderEstablishments;
        } else if (isHolder) {
            // Para sostenedor, mostrar solo sus establecimientos
            const holderEstablishments = establishmentList.filter(est =>
                est.projectId === userData?.tenantId
            );
            relevantEstablishments = holderEstablishments;
        } else {
            // Para establecimiento, mostrar solo su propio establecimiento
            relevantEstablishments = establishmentList.filter(est =>
                est.id === userData.establishmentId
            );
        }

        // Preparar datos de la tabla
        const data = relevantEstablishments.map(establishment => {
            const establishmentPME = pme.find(p =>
                p.establishmentId === establishment.id &&
                p.yearPME === yearSelected
            );

            if (!establishmentPME) {
                return {
                    rbd: establishment.rbd,
                    isEmpty: true
                };
            }

            const subsidyTotal = calculateSubsidyTotals(establishmentPME.incomeFromSubsidies, selectedSubsidy);
            const accruedTotal = establishmentPME.activities.reduce((total, activity) => {
                if (activity.subdimension === 'Remuneraciones') {
                    return total;
                }
                return total + calculateActivitySubsidyPercentage(activity, selectedSubsidy);
            }, 0);
            const salaryTotal = calculateSalaryExpenseBySubsidy(establishmentPME.salaryExpense, selectedSubsidy);

            // Obtener saldo del año anterior
            const previousYearPME = pmeList?.find(p =>
                p.establishmentId === establishment.id &&
                p.yearPME === yearSelected - 1
            );

            let previousYearBalance = "No Ingresado";
            if (previousYearPME?.incomeFromSubsidies) {
                console.log(previousYearPME);
                const prevSubsidyTotal = sumSubsidies(previousYearPME.incomeFromSubsidies);
                // Calcular gastos operacionales excluyendo remuneraciones del año anterior
                const prevAccruedTotal = previousYearPME.activities.reduce((total, activity) => {
                    if (activity.subdimension === 'Remuneraciones') {
                        return total;
                    }
                    return total + (activity.accruedBalance || 0);
                }, 0);
                const prevSalaryTotal = sumSalaryExpense(previousYearPME.salaryExpense);
                previousYearBalance = formatCurrency(prevSubsidyTotal - (prevAccruedTotal + prevSalaryTotal));
            }

            return {
                rbd: establishment.rbd,
                previousYearBalance,
                subsidyTotal,
                accruedTotal,
                salaryTotal,
                totalExpenses: accruedTotal + salaryTotal,
                currentBalance: subsidyTotal - (accruedTotal + salaryTotal),
                compliancePercentage: subsidyTotal ? ((accruedTotal + salaryTotal) * 100 / subsidyTotal).toFixed(1) : 0,
                salaryPercentage: subsidyTotal ? (salaryTotal * 100 / subsidyTotal).toFixed(1) : 0
            };
        });

        // Calcular totales
        const totals = data.reduce((acc, row) => {
            if (!row.isEmpty) {
                acc.subsidyTotal += row.subsidyTotal || 0;
                acc.accruedTotal += row.accruedTotal || 0;
                acc.salaryTotal += row.salaryTotal || 0;
                acc.totalExpenses += row.totalExpenses || 0;
                acc.currentBalance += row.currentBalance || 0;
            }
            return acc;
        }, {
            subsidyTotal: 0,
            accruedTotal: 0,
            salaryTotal: 0,
            totalExpenses: 0,
            currentBalance: 0
        });

        return {
            rows: data,
            totals: {
                ...totals,
                compliancePercentage: totals.subsidyTotal ?
                    ((totals.totalExpenses * 100) / totals.subsidyTotal).toFixed(1) : 0,
                salaryPercentage: totals.subsidyTotal ?
                    ((totals.salaryTotal * 100) / totals.subsidyTotal).toFixed(1) : 0
            }
        };
    }, [pme, yearSelected, selectedSubsidy, establishmentList, pmeList, isAdmin, isHolder, userData]);

    return (
        <table>
            <caption>CONSOLIDADO INGRESOS Y GASTOS ACTUALIZADOS PME {yearSelected} AL {getCurrentDate()}</caption>
            <thead>
                <tr>
                    <th>Nº</th>
                    <th>ESTABLECIMIENTO</th>
                    <th>SALDO POR RENDIR AÑO ANTERIOR {yearSelected - 1}</th>
                    <th>TOTAL SUBVENCIÓN RECIBIDA DEL AÑO {yearSelected}</th>
                    <th>GASTOS OPERACIONALES DEL AÑO {yearSelected}</th>
                    <th>GASTOS REMUNERACIONALES DEL AÑO {yearSelected}</th>
                    <th>TOTAL GASTOS PAGADOS DEL AÑO {yearSelected}</th>
                    <th>SALDO POR RENDIR AÑO {yearSelected}</th>
                    <th>% CUMPLIMIENTO</th>
                    <th>% GASTO REMUNERACIONES</th>
                </tr>
            </thead>
            <tbody>
                {tableData.rows.map((row, index) => (
                    <tr key={row.rbd}>
                        <td>{index + 1}</td>
                        <td>{row.rbd}</td>
                        <td>{row.isEmpty ? "No Ingresado" : row.previousYearBalance}</td>
                        <td>{row.isEmpty ? "-" : formatCurrency(row.subsidyTotal)}</td>
                        <td>{row.isEmpty ? "-" : formatCurrency(row.accruedTotal)}</td>
                        <td>{row.isEmpty ? "-" : formatCurrency(row.salaryTotal)}</td>
                        <td>{row.isEmpty ? "-" : formatCurrency(row.totalExpenses)}</td>
                        <td>{row.isEmpty ? "-" : formatCurrency(row.currentBalance)}</td>
                        <td>{row.isEmpty ? "-" : `${row.compliancePercentage}%`}</td>
                        <td>{row.isEmpty ? "-" : `${row.salaryPercentage}%`}</td>
                    </tr>
                ))}
                <tr className="totals-row">
                    <th colSpan="2">TOTALES</th>
                    <th>-</th>
                    <th>{formatCurrency(tableData.totals.subsidyTotal)}</th>
                    <th>{formatCurrency(tableData.totals.accruedTotal)}</th>
                    <th>{formatCurrency(tableData.totals.salaryTotal)}</th>
                    <th>{formatCurrency(tableData.totals.totalExpenses)}</th>
                    <th>{formatCurrency(tableData.totals.currentBalance)}</th>
                    <th>{`${tableData.totals.compliancePercentage}%`}</th>
                    <th>{`${tableData.totals.salaryPercentage}%`}</th>
                </tr>
            </tbody>
        </table>
    );
};

export default BudgetTable;